import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import getColorByRating from '@utils/review/getColorByRating';
import useIsdesktopLayout from '@hooks/useIsDesktopLayout';

function RatingLabel({ label, className }) {
  if (!label) return null;
  return <span className={className}>{label}</span>;
}

RatingLabel.propTypes = {
  label: PropTypes.string,
  className: PropTypes.string,
};

const StyledRatingLabel = styled(RatingLabel)`
  text-transform: uppercase;
  font-size: 10px;
`;

function RatingIcon({ color }) {
  return (
    <StyledRatingIcon
      xmlns="http://www.w3.org/2000/svg"
      width="8"
      height="8"
      viewBox="0 0 8 8"
    >
      <path
        fill={color}
        fillRule="evenodd"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 5.626L6.472 7l-.656-2.59L8 2.667l-2.876-.224L4 0 2.876 2.443 0 2.667 2.184 4.41 1.528 7z"
      />
    </StyledRatingIcon>
  );
}

RatingIcon.propTypes = {
  color: PropTypes.string.isRequired,
};

function RatingValue({ score, className }) {
  return (
    <StyledRatingSpan className={className}>
      {score > 0 ? score : '-  -'}
    </StyledRatingSpan>
  );
}

RatingValue.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  className: PropTypes.string,
};

const StyledRatingValueForCard = styled(RatingValue)`
  display: inline-block;
  font-size: 16px;
`;

const StyledRatingValueForScore = styled(RatingValue)`
  font-size: 16px;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.mainFont};
`;

const StyledRatingContainer = styled.div`
  display: flex;
  flex-direction: ${props => (props.isColumn ? 'column' : 'row')};
  align-items: center;
  color: ${props => props.color};
  margin-right: ${({ withExtraMargin }) => (withExtraMargin ? '32px' : '15px')};
  ${props =>
    props.forCard &&
    `
    font-weight: 800 !important;
    margin-top: 6px;
    `}
`;

const StyledRatingRowScore = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const StyledRatingIcon = styled.svg`
  margin-right: 4px;
`;

const StyledRatingSpan = styled.span`
  font-size: 16px;
  font-family: ${props => props.theme.fonts.mainFont};
  font-weight: 800;
`;

function RenderRatingValue({ score, forScore, forCard }) {
  if (forScore) return <StyledRatingValueForScore score={score} />;
  if (forCard) return <StyledRatingValueForCard score={score} />;
  return <RatingValue score={score} />;
}

RenderRatingValue.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  forScore: PropTypes.bool,
  forCard: PropTypes.bool,
};

function RatingScore({ color, score, orientation, forScore, forCard }) {
  if (orientation === 'column') {
    return (
      <StyledRatingRowScore>
        <RatingIcon color={color} />
        <RenderRatingValue
          score={score}
          forScore={forScore}
          forCard={forCard}
        />
      </StyledRatingRowScore>
    );
  }

  return (
    <>
      <RatingIcon color={color} />
      <RatingValue score={score} forScore={forScore} forCard={forCard} />
    </>
  );
}

RatingScore.propTypes = {
  color: PropTypes.string.isRequired,
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  orientation: PropTypes.string.isRequired,
  forScore: PropTypes.bool,
  forCard: PropTypes.bool,
};

function Rating({ score, label, orientation, forScore, forCard, className }) {
  const withExtraMargin = useIsdesktopLayout();
  const fixedScore = parseFloat(score).toFixed(1);
  const { textColor: color } = getColorByRating(parseFloat(fixedScore));

  return (
    <StyledRatingContainer
      isColumn={orientation === 'column'}
      forCard={forCard}
      color={color}
      className={className}
      withExtraMargin={withExtraMargin}
    >
      <RatingScore
        color={color}
        score={fixedScore}
        orientation={orientation}
        forScore={forScore}
        forCard={forCard}
      />
      <StyledRatingLabel label={label} />
    </StyledRatingContainer>
  );
}

Rating.defaultProps = {
  className: '',
  orientation: 'row',
};

Rating.propTypes = {
  score: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  orientation: PropTypes.oneOf(['row', 'column']),
  forScore: PropTypes.bool,
  forCard: PropTypes.bool,
};

export { getColorByRating };

export default styled(Rating)``;
